import React, { useState, useEffect } from 'react';
import { format } from 'date-fns';
import './OrderForm.css';

const IconEdit = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z" />
  </svg>
);

const IconCheck = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6" />
    <path d="M14 11v6" />
    <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
  </svg>
);

const IconUndo = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="1 4 1 10 7 10" />
    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10" />
  </svg>
);

const OrderForm = () => {
  const [orderItems, setOrderItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orderTab, setOrderTab] = useState('current');
  const [formData, setFormData] = useState({ item_name: '', quantity: '', vendor: '', notes: '' });
  const [editingItemId, setEditingItemId] = useState(null);
  const [editData, setEditData] = useState({ item_name: '', quantity: '', vendor: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const res = await fetch('/api/order-items?show_ordered=true');
      const data = await res.json();
      setOrderItems(data);
    } catch (error) {
      console.error('Error fetching order items:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddItem = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/order-items', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      if (res.ok) {
        setFormData({ item_name: '', quantity: '', vendor: '', notes: '' });
        fetchItems();
      }
    } catch (error) {
      console.error('Error adding item:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateItem = async (itemId) => {
    try {
      const res = await fetch(`/api/order-items/${itemId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData)
      });
      if (res.ok) {
        setEditingItemId(null);
        fetchItems();
      }
    } catch (error) {
      console.error('Error updating item:', error);
    }
  };

  const handleToggleOrdered = async (itemId, isOrdered) => {
    try {
      await fetch(`/api/order-items/${itemId}/ordered`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_ordered: !isOrdered })
      });
      fetchItems();
    } catch (error) {
      console.error('Error toggling ordered status:', error);
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Delete this item?')) return;
    try {
      await fetch(`/api/order-items/${itemId}`, { method: 'DELETE' });
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const currentItems = orderItems.filter(i => !i.is_ordered);
  const historyItems = orderItems.filter(i => i.is_ordered);

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="order-form-page">
      <div className="page-header">
        <h1 className="page-header__title">Order Form</h1>
      </div>

      <div className="order-form-layout">

        {/* Add Item Panel */}
        <div className="order-form-add-panel">
          <h2 className="order-form-panel-title">Add Item</h2>
          <form onSubmit={handleAddItem}>
            <div className="form-group">
              <label className="form-label">Item Name *</label>
              <input
                type="text"
                className="form-input"
                value={formData.item_name}
                onChange={(e) => setFormData({ ...formData, item_name: e.target.value })}
                placeholder="e.g., Microfiber towels, Soap..."
                required
              />
            </div>
            <div className="form-group">
              <label className="form-label">Quantity</label>
              <input
                type="text"
                className="form-input"
                value={formData.quantity}
                onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                placeholder="e.g., 2 cases, 5 gallons..."
              />
            </div>
            <div className="form-group">
              <label className="form-label">Preferred Vendor</label>
              <input
                type="text"
                className="form-input"
                value={formData.vendor}
                onChange={(e) => setFormData({ ...formData, vendor: e.target.value })}
                placeholder="e.g., Amazon, Detail King..."
              />
            </div>
            <div className="form-group">
              <label className="form-label">Notes</label>
              <textarea
                className="form-textarea"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows="3"
                placeholder="Any additional notes..."
              />
            </div>
            <button type="submit" className="btn btn--primary order-form-submit" disabled={submitting}>
              {submitting ? 'Adding...' : '+ Add Item'}
            </button>
          </form>
        </div>

        {/* List Panel */}
        <div className="order-form-list-panel">

          {/* Tabs */}
          <div className="order-form-tabs">
            <button
              type="button"
              className={`order-form-tab ${orderTab === 'current' ? 'order-form-tab--active' : ''}`}
              onClick={() => setOrderTab('current')}
            >
              Current List ({currentItems.length})
            </button>
            <button
              type="button"
              className={`order-form-tab ${orderTab === 'history' ? 'order-form-tab--active' : ''}`}
              onClick={() => setOrderTab('history')}
            >
              History ({historyItems.length})
            </button>
          </div>

          {/* Current List */}
          {orderTab === 'current' && (
            <div className="order-form-items">
              {currentItems.length === 0 ? (
                <p className="order-form-empty">No items on the current list.</p>
              ) : currentItems.map((item) => (
                <div
                  key={item.id}
                  className={`order-form-item ${editingItemId === item.id ? 'order-form-item--editing' : ''}`}
                >
                  {editingItemId === item.id ? (
                    <div className="order-form-item__edit">
                      <input
                        type="text"
                        className="form-input"
                        value={editData.item_name}
                        onChange={(e) => setEditData({ ...editData, item_name: e.target.value })}
                        placeholder="Item name"
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={editData.quantity}
                        onChange={(e) => setEditData({ ...editData, quantity: e.target.value })}
                        placeholder="Quantity"
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={editData.vendor}
                        onChange={(e) => setEditData({ ...editData, vendor: e.target.value })}
                        placeholder="Preferred vendor"
                      />
                      <input
                        type="text"
                        className="form-input"
                        value={editData.notes}
                        onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                        placeholder="Notes"
                      />
                      <div className="order-form-item__actions">
                        <button type="button" className="btn btn--primary btn--small" onClick={() => handleUpdateItem(item.id)}>Save</button>
                        <button type="button" className="btn btn--secondary btn--small" onClick={() => setEditingItemId(null)}>Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="order-form-item__view">
                      <div className="order-form-item__info">
                        <span className="order-form-item__name">{item.item_name}</span>
                        {item.quantity && <span className="order-form-item__qty">— {item.quantity}</span>}
                        {item.vendor && <div className="order-form-item__meta">Vendor: {item.vendor}</div>}
                        {item.notes && <div className="order-form-item__meta">{item.notes}</div>}
                      </div>
                      <div className="order-form-item__actions">
                        <button
                          type="button"
                          className="order-form-icon-btn"
                          onClick={() => { setEditingItemId(item.id); setEditData({ item_name: item.item_name, quantity: item.quantity || '', vendor: item.vendor || '', notes: item.notes || '' }); }}
                          title="Edit"
                          aria-label="Edit item"
                        >
                          <IconEdit />
                        </button>
                        <button
                          type="button"
                          className="order-form-icon-btn order-form-icon-btn--ordered"
                          onClick={() => handleToggleOrdered(item.id, item.is_ordered)}
                          title="Mark Ordered"
                          aria-label="Mark ordered"
                        >
                          <IconCheck />
                        </button>
                        <button
                          type="button"
                          className="order-form-icon-btn order-form-icon-btn--delete"
                          onClick={() => handleDeleteItem(item.id)}
                          title="Delete"
                          aria-label="Delete item"
                        >
                          <IconTrash />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* History */}
          {orderTab === 'history' && (
            <div className="order-form-items">
              {historyItems.length === 0 ? (
                <p className="order-form-empty">No order history yet.</p>
              ) : historyItems.map((item) => (
                <div key={item.id} className="order-form-item order-form-item--ordered">
                  <div className="order-form-item__view">
                    <div className="order-form-item__info">
                      <span className="order-form-item__name order-form-item__name--struck">{item.item_name}</span>
                      {item.quantity && <span className="order-form-item__qty order-form-item__name--struck">— {item.quantity}</span>}
                      {item.vendor && <div className="order-form-item__meta">Vendor: {item.vendor}</div>}
                      {item.notes && <div className="order-form-item__meta">{item.notes}</div>}
                      {item.ordered_at && (
                        <div className="order-form-item__date">
                          Ordered: {format(new Date(item.ordered_at), 'MMM d, yyyy')}
                        </div>
                      )}
                    </div>
                    <div className="order-form-item__actions">
                      <button
                        type="button"
                        className="order-form-icon-btn"
                        onClick={() => handleToggleOrdered(item.id, item.is_ordered)}
                        title="Move Back"
                        aria-label="Move back to current list"
                      >
                        <IconUndo />
                      </button>
                      <button
                        type="button"
                        className="order-form-icon-btn order-form-icon-btn--delete"
                        onClick={() => handleDeleteItem(item.id)}
                        title="Delete"
                        aria-label="Delete item"
                      >
                        <IconTrash />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default OrderForm;
